import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { Loader2, Handshake, Mail, Phone, Linkedin } from "lucide-react";

const STATUS_META = {
  pending_admin: { label: "بانتظار اعتماد الإدارة", tone: "bg-amber-50 text-amber-700 border-amber-100" },
  student_review: { label: "بانتظار موافقة الطالب", tone: "bg-blue-50 text-blue-700 border-blue-100" },
  approved: { label: "موافَق عليه", tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  rejected: { label: "مرفوض", tone: "bg-red-50 text-red-700 border-red-100" },
};

export default function RecruiterRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/recruiter/contact-requests");
      setItems(data.requests || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <DashboardLayout>
      <div data-testid="recruiter-contact-requests">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">طلبات التواصل</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">طلبات التواصل التي أرسلتَها</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            كل طلب يمر عبر اعتماد الإدارة، ثم موافقة الطالب. ستظهر بيانات التواصل هنا فقط عند وصول الطلب لحالة «موافَق عليه».
          </p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="recruiter-cr-error" />
        ) : items.length === 0 ? (
          <EmptyState icon={Handshake} title="لم ترسل أي طلبات بعد" description="ابدأ من لوحة الخريجين لإرسال طلب تواصل مع خريج مؤهّل." testid="recruiter-cr-empty" />
        ) : (
          <div className="grid gap-3">
            {items.map((r) => {
              const meta = STATUS_META[r.status] || { label: r.status, tone: "bg-slate-100 text-slate-700" };
              return (
                <div key={r.id} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`recruiter-cr-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs text-slate-500">الطالب</div>
                      <div className="font-heading font-bold text-slate-900">{r.student_name}</div>
                    </div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${meta.tone}`}>{meta.label}</span>
                  </div>
                  <div className="mt-3 p-3 rounded-lg bg-cream-50 text-sm text-slate-700 whitespace-pre-wrap">
                    {r.message || "—"}
                  </div>
                  {r.admin_note && (
                    <div className="mt-2 text-xs text-slate-500">ملاحظة الإدارة: <span className="text-slate-700">{r.admin_note}</span></div>
                  )}
                  {r.status === "approved" && (
                    <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-100" data-testid={`recruiter-cr-contact-${r.id}`}>
                      <div className="text-xs font-bold text-emerald-800 mb-2">بيانات التواصل</div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-800">
                        {r.student_email && (
                          <a href={`mailto:${r.student_email}`} className="inline-flex items-center gap-1.5 hover:text-teal-700" dir="ltr">
                            <Mail className="h-4 w-4" /> {r.student_email}
                          </a>
                        )}
                        {r.student_phone && (
                          <a href={`tel:${r.student_phone}`} className="inline-flex items-center gap-1.5 hover:text-teal-700" dir="ltr">
                            <Phone className="h-4 w-4" /> {r.student_phone}
                          </a>
                        )}
                        {r.student_linkedin && (
                          <a href={r.student_linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-teal-700">
                            <Linkedin className="h-4 w-4" /> LinkedIn
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  {r.status === "rejected" && (
                    <div className="mt-3 text-sm text-red-700">لن تُكشف بيانات التواصل — الطلب مرفوض.</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
